/**
 * Message Handler Utility
 * 
 * This utility provides functions to get user-friendly messages
 * and handle API errors consistently across the application.
 */

import { showSuccess, showError, showInfo } from "toast/toast";
import {
  MESSAGE_MAP,
  ERROR_CODE_MAP,
  COMMON_MESSAGES,
  ADDRESSADDEDIT_MESSAGES,
  AGENTOFFICEPRODUCTADDEDIT_MESSAGES,
  SignageSimpleProductADDEDIT_MESSAGES,
  WAREHOUSE_MESSAGES,
  ZONE_MESSAGES,
  RACK_MESSAGES,
  BIN_MESSAGES,
  BROKERAGE_FIRM_MESSAGES,
  AGENT_MESSAGES,
  EntityType,
  OperationType,
} from "./messages";

/**
 * Get success message for a specific entity and operation
 */
export const getSuccessMessage = (
  entity: EntityType,
  operation: OperationType
): string => {
  return MESSAGE_MAP[entity]?.[operation]?.success || "Operation completed successfully!";
};

/**
 * Get error message for a specific entity and operation
 */
export const getErrorMessage = (
  entity: EntityType,
  operation: OperationType
): string => {
  return MESSAGE_MAP[entity]?.[operation]?.error || COMMON_MESSAGES.unknownError;
};

/**
 * Get generic error message for a specific entity and operation
 */
export const getGenericErrorMessage = (
  entity: EntityType,
  operation: OperationType
): string => {
  return MESSAGE_MAP[entity]?.[operation]?.errorGeneric || COMMON_MESSAGES.unknownError;
};

/**
 * Map backend error to user-friendly message
 */
export const mapBackendError = (error: unknown): string => {
  if (!error) {
    return COMMON_MESSAGES.unknownError;
  }

  // Handle Error objects
  if (error instanceof Error) {
    const errorMessage = error.message;
    
    // Check if we have a direct mapping
    if (ERROR_CODE_MAP[errorMessage]) {
      return ERROR_CODE_MAP[errorMessage];
    }
    
    // Check for partial matches (case-insensitive)
    const lowerMessage = errorMessage.toLowerCase();
    for (const [key, value] of Object.entries(ERROR_CODE_MAP)) {
      if (lowerMessage.includes(key.toLowerCase())) {
        return value;
      }
    }
    
    // Return the error message if it's user-friendly, otherwise return generic
    // You can add more logic here to detect if the message is user-friendly
    if (errorMessage.length < 200 && !errorMessage.includes("at ") && !errorMessage.includes("Error:")) {
      return errorMessage;
    }
    
    return COMMON_MESSAGES.unknownError;
  }

  // Handle string errors
  if (typeof error === "string") {
    if (ERROR_CODE_MAP[error]) {
      return ERROR_CODE_MAP[error];
    }
    return error;
  }

  // Handle objects with message property
  if (typeof error === "object" && error !== null) {
    const errorObj = error as any;
    if (errorObj.message) {
      return mapBackendError(errorObj.message);
    }
    if (errorObj.error) {
      return mapBackendError(errorObj.error);
    }
  }

  return COMMON_MESSAGES.unknownError;
};

/**
 * Show success message for CRUD operation
 */
export const showOperationSuccess = (
  entity: EntityType,
  operation: OperationType
): void => {
  const message = getSuccessMessage(entity, operation);
  showSuccess(message);
};

/**
 * Show error message for CRUD operation with optional backend error handling
 */
export const showOperationError = (
  entity: EntityType,
  operation: OperationType,
  backendError?: unknown,
  useBackendMessage: boolean = false
): void => {
  let message: string;
  
  if (useBackendMessage && backendError) {
    const mappedError = mapBackendError(backendError);
    // Use backend message if it's different from generic error, otherwise use entity-specific error
    message = mappedError !== COMMON_MESSAGES.unknownError 
      ? mappedError 
      : getErrorMessage(entity, operation);
  } else {
    message = getErrorMessage(entity, operation);
  }
  
  showError(message);
};

/**
 * Show info message
 */
export const showOperationInfo = (message: string): void => {
  showInfo(message);
};

/**
 * Handle API response and show appropriate message
 * 
 * @param response - Fetch Response object
 * @param entity - Entity type (teammember, agent, brokeragefirm)
 * @param operation - Operation type (create, update, delete, fetch, fetchSingle)
 * @param options - Options for handling the response
 */
export const handleApiResponse = async (
  response: Response,
  entity: EntityType,
  operation: OperationType,
  options: {
    showSuccessOnSuccess?: boolean;
    showErrorOnFailure?: boolean;
    parseJson?: boolean;
  } = {}
): Promise<any> => {
  const {
    showSuccessOnSuccess = true,
    showErrorOnFailure = true,
    parseJson = true,
  } = options;

  try {
    if (!response.ok) {
      let errorMessage = `Operation failed (${response.status})`;
      
      try {
        const errorData = await response.json();
        errorMessage = errorData?.error || errorData?.message || errorMessage;
      } catch {
        // If JSON parsing fails, use status text
        errorMessage = response.statusText || errorMessage;
      }

      if (showErrorOnFailure) {
        const mappedError = mapBackendError(errorMessage);
        showError(
          mappedError !== COMMON_MESSAGES.unknownError
            ? mappedError
            : getErrorMessage(entity, operation)
        );
      }

      throw new Error(errorMessage);
    }

    if (showSuccessOnSuccess && operation !== "fetch" && operation !== "fetchSingle") {
      showOperationSuccess(entity, operation);
    }

    if (parseJson) {
      return await response.json();
    }

    return response;
  } catch (error) {
    if (showErrorOnFailure && !(error instanceof Error && error.message.includes("Operation failed"))) {
      showOperationError(entity, operation, error);
    }
    throw error;
  }
};

/**
 * Wrapper function for CRUD operations that automatically handles messages
 * 
 * Usage example:
 * ```typescript
 * try {
 *   const result = await handleCrudOperation(
 *     () => fetch(url, options),
 *     "teammember",
 *     "create"
 *   );
 *   // Handle result...
 * } catch (error) {
 *   // Error already shown, just handle cleanup
 * }
 * ```
 */
export const handleCrudOperation = async <T>(
  apiCall: () => Promise<Response>,
  entity: EntityType,
  operation: OperationType,
  options: {
    showSuccess?: boolean;
    showError?: boolean;
  } = {}
): Promise<T> => {
  const { showSuccess = true, showError = true } = options;

  try {
    const response = await apiCall();
    return await handleApiResponse(response, entity, operation, {
      showSuccessOnSuccess: showSuccess,
      showErrorOnFailure: showError,
      parseJson: true,
    });
  } catch (error) {
    if (showError) {
      showOperationError(entity, operation, error, true);
    }
    throw error;
  }
};

/**
 * Get address message
 */
export const getAddressMessage = (operation: "create" | "update" | "fetch", type: "success" | "error" | "errorGeneric"): string => {
  return ADDRESSADDEDIT_MESSAGES[operation]?.[type] || COMMON_MESSAGES.unknownError;
};

/**
 * Get product message
 */
export const getProductMessage = (operation: "create" | "update" | "fetch", type: "success" | "error" | "errorGeneric"): string => {
  return AGENTOFFICEPRODUCTADDEDIT_MESSAGES[operation]?.[type] || COMMON_MESSAGES.unknownError;
};

/**
 * Get SignageSimpleproduct message
 */
export const getSignageSimpleproductMessage = (operation: "create" | "update" | "fetch", type: "success" | "error" | "errorGeneric"): string => {
  return SignageSimpleProductADDEDIT_MESSAGES[operation]?.[type] || COMMON_MESSAGES.unknownError;
};
/**
 * Get warehouse message
 */
export const getWarehouseMessage = (operation: "create" | "update" | "delete" | "fetch" | "fetchSingle", type: "success" | "error" | "errorGeneric"): string => {
  return WAREHOUSE_MESSAGES[operation]?.[type] || COMMON_MESSAGES.unknownError;
};

/**
 * Get zone message
 */
export const getZoneMessage = (operation: "create" | "update" | "delete" | "fetch", type: "success" | "error" | "errorGeneric"): string => {
  return ZONE_MESSAGES[operation]?.[type] || COMMON_MESSAGES.unknownError;
};

/**
 * Get rack message
 */
export const getRackMessage = (operation: "create" | "update" | "delete" | "fetch", type: "success" | "error" | "errorGeneric"): string => {
  return RACK_MESSAGES[operation]?.[type] || COMMON_MESSAGES.unknownError;
};

/**
 * Get bin message
 */
export const getBinMessage = (operation: "create" | "update" | "delete" | "fetch", type: "success" | "error" | "errorGeneric"): string => {
  return BIN_MESSAGES[operation]?.[type] || COMMON_MESSAGES.unknownError;
};

/**
 * Get brokerage firm license message
 */
export const getBrokerageFirmLicenseMessage = (operation: "activateLicense" | "deactivateLicense", type: "success" | "error" | "errorGeneric"): string => {
  return BROKERAGE_FIRM_MESSAGES[operation]?.[type] || COMMON_MESSAGES.unknownError;
};

/**
 * Show address success/error message
 */
export const showAddressMessage = (operation: "create" | "update" | "fetch", isSuccess: boolean, backendError?: unknown): void => {
  if (isSuccess) {
    showSuccess(getAddressMessage(operation, "success"));
  } else {
    const errorMsg = backendError 
      ? mapBackendError(backendError)
      : getAddressMessage(operation, "error");
    showError(errorMsg);
  }
};

/**
 * Show product success/error message
 */
export const showProductMessage = (operation: "create" | "update" | "fetch", isSuccess: boolean, backendError?: unknown): void => {
  if (isSuccess) {
    showSuccess(getProductMessage(operation, "success"));
  } else {
    const errorMsg = backendError 
      ? mapBackendError(backendError)
      : getProductMessage(operation, "error");
    showError(errorMsg);
  }
};

/**
 * Convenience functions for common operations
 */
export const messageHandler = {
  // Show messages
  success: (entity: EntityType, operation: OperationType) =>
    showOperationSuccess(entity, operation),
  
  error: (entity: EntityType, operation: OperationType, backendError?: unknown) =>
    showOperationError(entity, operation, backendError, true),
  
  info: (message: string) => showOperationInfo(message),
  
  // Address messages
  address: {
    success: (operation: "create" | "update" | "fetch" | "delete") => 
      showSuccess(operation === "delete" ? ADDRESSADDEDIT_MESSAGES.update.success : getAddressMessage(operation, "success")),
    error: (operation: "create" | "update" | "fetch" | "delete", backendError?: unknown) => {
      const errorMsg = backendError 
        ? mapBackendError(backendError)
        : operation === "delete" ? ADDRESSADDEDIT_MESSAGES.update.error : getAddressMessage(operation, "error");
      showError(errorMsg);
    },
    getMessage: getAddressMessage,
  },
  
  // Agent messages
  agent: {
    success: (operation: OperationType) => 
      showSuccess(AGENT_MESSAGES[operation]?.success || COMMON_MESSAGES.unknownError),
    error: (operation: OperationType, backendError?: unknown) => {
      const errorMsg = backendError 
        ? mapBackendError(backendError)
        : AGENT_MESSAGES[operation]?.error || COMMON_MESSAGES.unknownError;
      showError(errorMsg);
    },
  },
  
  // Product messages
  product: {
    success: (operation: "create" | "update" | "fetch" | "delete") => 
      showSuccess(operation === "delete" ? AGENTOFFICEPRODUCTADDEDIT_MESSAGES.update.success : getProductMessage(operation, "success")),
    error: (operation: "create" | "update" | "fetch" | "delete", backendError?: unknown) => {
      const errorMsg = backendError 
        ? mapBackendError(backendError)
        : operation === "delete" ? AGENTOFFICEPRODUCTADDEDIT_MESSAGES.update.error : getProductMessage(operation, "error");
      showError(errorMsg);
    },
    getMessage: getProductMessage,
    validation: AGENTOFFICEPRODUCTADDEDIT_MESSAGES.validation,
    image: AGENTOFFICEPRODUCTADDEDIT_MESSAGES.image,
    product: AGENTOFFICEPRODUCTADDEDIT_MESSAGES.product,
    category: AGENTOFFICEPRODUCTADDEDIT_MESSAGES.category,
  },


    // Product messages
  simpleproduct: {
    success: (operation: "create" | "update" | "fetch" | "delete") => 
      showSuccess(operation === "delete" ? AGENTOFFICEPRODUCTADDEDIT_MESSAGES.update.success : getProductMessage(operation, "success")),
    error: (operation: "create" | "update" | "fetch" | "delete", backendError?: unknown) => {
      const errorMsg = backendError 
        ? mapBackendError(backendError)
        : operation === "delete" ? AGENTOFFICEPRODUCTADDEDIT_MESSAGES.update.error : getProductMessage(operation, "error");
      showError(errorMsg);
    },
    getMessage: getSignageSimpleproductMessage,
    validation: AGENTOFFICEPRODUCTADDEDIT_MESSAGES.validation,
    image: AGENTOFFICEPRODUCTADDEDIT_MESSAGES.image,
    product: AGENTOFFICEPRODUCTADDEDIT_MESSAGES.product,
    category: AGENTOFFICEPRODUCTADDEDIT_MESSAGES.category,
  },
  
  // Warehouse messages
  warehouse: {
    success: (operation: "create" | "update" | "delete" | "fetch" | "fetchSingle") => 
      showSuccess(getWarehouseMessage(operation, "success")),
    error: (operation: "create" | "update" | "delete" | "fetch" | "fetchSingle", backendError?: unknown) => {
      const errorMsg = backendError 
        ? mapBackendError(backendError)
        : getWarehouseMessage(operation, "error");
      showError(errorMsg);
    },
    getMessage: getWarehouseMessage,
  },
  
  // Zone messages
  zone: {
    success: (operation: "create" | "update" | "delete" | "fetch") => 
      showSuccess(getZoneMessage(operation, "success")),
    error: (operation: "create" | "update" | "delete" | "fetch", backendError?: unknown) => {
      const errorMsg = backendError 
        ? mapBackendError(backendError)
        : getZoneMessage(operation, "error");
      showError(errorMsg);
    },
    getMessage: getZoneMessage,
    validation: ZONE_MESSAGES.validation,
    deleteError: ZONE_MESSAGES.deleteError,
  },
  
  // Rack messages
  rack: {
    success: (operation: "create" | "update" | "delete" | "fetch") => 
      showSuccess(getRackMessage(operation, "success")),
    error: (operation: "create" | "update" | "delete" | "fetch", backendError?: unknown) => {
      const errorMsg = backendError 
        ? mapBackendError(backendError)
        : getRackMessage(operation, "error");
      showError(errorMsg);
    },
    getMessage: getRackMessage,
    validation: RACK_MESSAGES.validation,
    deleteError: RACK_MESSAGES.deleteError,
  },
  
  // Bin messages
  bin: {
    success: (operation: "create" | "update" | "delete" | "fetch") => 
      showSuccess(getBinMessage(operation, "success")),
    error: (operation: "create" | "update" | "delete" | "fetch", backendError?: unknown) => {
      const errorMsg = backendError 
        ? mapBackendError(backendError)
        : getBinMessage(operation, "error");
      showError(errorMsg);
    },
    getMessage: getBinMessage,
    validation: BIN_MESSAGES.validation,
  },
  
  // Brokerage firm messages
  brokerageFirm: {
    success: (operation: OperationType) => 
      showSuccess(BROKERAGE_FIRM_MESSAGES[operation]?.success || COMMON_MESSAGES.unknownError),
    error: (operation: OperationType, backendError?: unknown) => {
      const errorMsg = backendError 
        ? mapBackendError(backendError)
        : BROKERAGE_FIRM_MESSAGES[operation]?.error || COMMON_MESSAGES.unknownError;
      showError(errorMsg);
    },
    activateLicense: {
      success: () => showSuccess(BROKERAGE_FIRM_MESSAGES.activateLicense.success),
      error: (backendError?: unknown) => {
        const errorMsg = backendError 
          ? mapBackendError(backendError)
          : BROKERAGE_FIRM_MESSAGES.activateLicense.error;
        showError(errorMsg);
      },
    },
    deactivateLicense: {
      success: () => showSuccess(BROKERAGE_FIRM_MESSAGES.deactivateLicense.success),
      error: (backendError?: unknown) => {
        const errorMsg = backendError 
          ? mapBackendError(backendError)
          : BROKERAGE_FIRM_MESSAGES.deactivateLicense.error;
        showError(errorMsg);
      },
    },
  },
  
  // Get messages (without showing)
  getSuccess: (entity: EntityType, operation: OperationType) =>
    getSuccessMessage(entity, operation),
  
  getError: (entity: EntityType, operation: OperationType) =>
    getErrorMessage(entity, operation),
  
  // Map backend errors
  mapError: (error: unknown) => mapBackendError(error),
  
  // Handle API responses
  handleResponse: handleApiResponse,
  
  // Handle CRUD operations
  handleCrud: handleCrudOperation,
};

